/**
 * Help (1.2.0): the guides for using PRISM, the administration guides, the integration API reference and About,
 * one tab each. The guides live in help/guides/ and are listed in help/guides.ts with who may see them: Help shows
 * only what the signed-in person can use. /help opens the Introduction; a hidden or unknown guide's address opens the
 * first guide the person can see.
 *
 * 1.3.0 fix: which guide to show is decided by helpTarget (guides.ts), which waits for the signed-in person before
 * judging a guide hidden and never redirects to the address it is on; and the guide sits in an error boundary, so a
 * guide that throws or whose code fails to load shows the error (and reloads once for the latter) instead of
 * unmounting the whole app to a blank page.
 */
import { Suspense } from 'react';
import { NavLink, Navigate, useLocation } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useMe } from '../../api/hooks';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { OverflowTabs } from '../../components/OverflowTabs';
import { helpTarget, visibleGuides } from './guides';

const Loading = () => <div className="skeleton" style={{ height: 200 }} data-testid="help-loading" />;

export function Help() {
  const { pathname } = useLocation();
  const me = useMe().data;
  const guides = me ? visibleGuides(me.permissions) : [];
  const target = helpTarget(pathname.replace(/^\/help\/?/, ''), me?.permissions);
  return (
    <>
      <header className="topbar">
        <h1><BookOpen size={16} style={{ verticalAlign: '-2px' }} /> Help</h1>
        <OverflowTabs as="nav" className="admin-tabs" wrapClassName="admin-tabs-wrap" label="Help sections" activeKey={pathname} testId="help-tabs">
          {guides.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={`/help/${to}`} className={({ isActive }) => `admin-tab ${isActive ? 'active' : ''}`} data-testid={`help-tab-${to}`}><Icon size={15} />{label}</NavLink>
          ))}
        </OverflowTabs>
      </header>
      <div className="page help-page">
        <ErrorBoundary resetKey={pathname} testId="help-error" title="This guide could not be shown">
          <Suspense fallback={<Loading />}>
            {target.kind === 'wait' ? <Loading />
              : target.kind === 'redirect' ? <Navigate to={target.to} replace />
              : <target.guide.Component key={target.guide.to} />}
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
}
